import { api } from '@/api';
import router from '@/router';
import { getLocalToken, removeLocalToken, removeWZFilters, saveLocalToken } from '@/utils';
import { AxiosError } from 'axios';
import { getStoreAccessors } from 'typesafe-vuex';
import { ActionContext } from 'vuex';
import { State } from '../state';
import {
    commitAddNotification,
    commitRemoveNotification, commitSetActionLogsResponse, commitSetAppConfigs, commitSetDropdownMenu,
    commitSetLoggedIn,
    commitSetLogInError, commitSetMainMenu,
    commitSetStructureResponse,
    commitSetStructureUserDetails,
    commitSetToken,
    commitSetUserProfile,
    commitSetWaitingUsersResponse,
} from './mutations';
import { AppNotification, MainState } from './state';
import {readUserProfile} from '@/store/main/getters';

type MainContext = ActionContext<MainState, State>;

export const actions = {
    async actionLogIn(context: MainContext, payload: { username: string; password: string }) {
        try {
            const response = await api.logInGetToken(payload.username, payload.password);
            const token = response.data.token;
            if (token) {
                saveLocalToken(token);
                commitSetToken(context, token);
                commitSetLoggedIn(context, true);
                commitSetLogInError(context, false);
                await dispatchGetUserProfile(context);
                const userProfile = readUserProfile(context);
                // if (userProfile && userProfile.is_subscribed) {
                await dispatchRouteLoggedIn(context, {path: '/dashboard'});
                // } else {
                //     await dispatchRouteLoggedIn(context, {path: '/subscription'});
                // }
                commitAddNotification(context, { content: 'Logged in', color: 'success' });
            } else {
                await dispatchLogOut(context);
            }
        } catch (err) {
            commitSetLogInError(context, true);
            await dispatchLogOut(context);
        }
    },
    async actionTwoFactorLogIn(
        context: MainContext,
        payload: { username: string; password: string, auth_step: string, code?: string }
    ) {
        try {
            const response = await api.twoFactorAuth(payload);
            const token = response.data.token;
            if (token) {
                if (!response.data.user.email_activation) {
                    router.push('/email-required');
                    return null;
                }
                saveLocalToken(token);
                commitSetToken(context, token);
                commitSetLoggedIn(context, true);
                commitSetLogInError(context, false);
                await dispatchGetUserProfile(context);
                const userProfile = readUserProfile(context);
                // if (userProfile && userProfile.is_subscribed) {
                await dispatchRouteLoggedIn(context, {path: '/dashboard'});
                // } else {
                //     await dispatchRouteLoggedIn(context, {path: '/subscription'});
                // }
                commitAddNotification(context, { content: 'Logged in', color: 'success' });
                return response.data;
            } else {
                return response.data;
            }
        } catch (err) {
            commitSetLogInError(context, true);
            await dispatchLogOut(context);
            return null;
        }
    },
    async actionRegister(
        context: MainContext, payload: { name: string, email: string; password: string; phone: string; promo: string, country: string },
    ) {
        try {
            const response = await api.registerGetToken(payload);
            if (response.status == 200) {
                commitSetLogInError(context, true);
                return;
            }
            const token = response.data.token;
            if (token) {
                if (!response.data.user.email_activation) {
                    router.push('/email-required');
                    return null;
                }
                saveLocalToken(token);
                commitSetToken(context, token);
                commitSetLoggedIn(context, true);
                commitSetLogInError(context, false);
                await dispatchGetUserProfile(context);
                const userProfile = readUserProfile(context);
                // if (userProfile && userProfile.is_subscribed) {
                await dispatchRouteLoggedIn(context, {path: '/dashboard'});
                // } else {
                //     await dispatchRouteLoggedIn(context, {path: '/subscription'});
                // }
                commitAddNotification(context, { content: 'Logged in', color: 'success' });
            } else {
                await dispatchLogOut(context);
            }
        } catch (err) {
            commitSetLogInError(context, true);
        }
    },
    async actionLogInByToken(context: MainContext, payload: { authToken: string, path: string }) {
        try {
            const response = await api.logInByToken(payload.authToken);
            const token = response.data.token;
            if (token) {
                if (!response.data.user.email_activation) {
                    router.push('/email-required');
                    return null;
                }
                saveLocalToken(token);
                commitSetToken(context, token);
                commitSetLoggedIn(context, true);
                commitSetLogInError(context, false);
                await dispatchGetUserProfile(context);
                await dispatchRouteLoggedIn(context, {path: payload.path});
                commitAddNotification(context, { content: 'Logged in', color: 'success' });
            } else {
                await dispatchLogOut(context);
            }
        } catch (err) {
            commitSetLogInError(context, true);
            await dispatchLogOut(context);
        }
    },
    async actionSubmitResetCredsByToken(
        context: MainContext, payload: { token: string, email: string, password: string },
    ) {
        try {
            const response = await api.submitResetCredsByToken(
                payload,
            );
            const token = response.data.token;
            if (token) {
                saveLocalToken(token);
                commitSetToken(context, token);
                commitSetLoggedIn(context, true);
                await dispatchGetUserProfile(context);
                await dispatchRouteLoggedIn(context, {path: '/dashboard'});
                return response.status;
            } else {
                // await dispatchLogOut(context);
                return response.status;
            }
        } catch (err) {
            // await dispatchLogOut(context);
            return err.response.status;
        }
    },
    async actionGetUserProfile(context: MainContext) {
        try {
            const response = await api.getMe(context.state.token);
            if (response.data) {
                commitSetUserProfile(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateUserProfile(context: MainContext, payload) {
        try {
            const loadingNotification = { content: 'saving', showProgress: true };
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.updateMe(context.state.token, payload),
                await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            saveLocalToken(response.data.token);
            commitSetToken(context, response.data.token);
            commitSetLoggedIn(context, true);
            commitSetUserProfile(context, response.data.user);
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'Profile successfully updated', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionConnectGoogleAuthenticator(context: MainContext) {
        try {
            const response = await api.connectGoogleAuthenticator(context.state.token);
            return response.data;
        } catch (error) {
            await dispatchCheckApiError(context, error);
            return '';
        }
    },
    async actionDisconnectGoogleAuthenticator(context: MainContext) {
        try {
            const response = await api.disconnectGoogleAuthenticator(context.state.token);
            commitSetUserProfile(context, response.data);
            return true;
        } catch (error) {
            await dispatchCheckApiError(context, error);
            return false;
        }
    },
    async updateUserAvatar(context: MainContext, payload: { data: any }) {
        try {
            const response = (await Promise.all([
                api.updateUserAvatar(context.state.token, payload.data),
                await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitSetUserProfile(context, response.data);
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCheckLoggedIn(context: MainContext) {
        if (!context.state.isLoggedIn) {
            let token = context.state.token;
            if (!token) {
                const localToken = getLocalToken();
                if (localToken) {
                    commitSetToken(context, localToken);
                    token = localToken;
                }
            }
            if (token) {
                try {
                    const response = await api.getMe(token);
                    commitSetLoggedIn(context, true);
                    commitSetUserProfile(context, response.data);
                } catch (error) {
                    await dispatchRemoveLogIn(context);
                }
            } else {
                await dispatchRemoveLogIn(context);
            }
        }
    },
    async actionRemoveLogIn(context: MainContext) {
        // removeWZFilters();
        removeLocalToken();
        commitSetToken(context, '');
        commitSetLoggedIn(context, false);
    },
    async actionLogOut(context: MainContext) {
        await dispatchRemoveLogIn(context);
        await dispatchRouteLogOut(context);
    },
    async actionUserLogOut(context: MainContext) {
        await dispatchLogOut(context);
        commitAddNotification(context, { content: 'Logged out', color: 'success' });
    },
    actionRouteLogOut(context: MainContext) {
        if (router.currentRoute.path !== '/login' && !router.currentRoute.path.includes('/register')
            && !router.currentRoute.path.includes('/recover-password')
            && !router.currentRoute.path.includes('/reset-credentials')
            && !router.currentRoute.path.includes('/password-reset')
        ) {
            router.push('/login');
        }
    },
    async actionCheckApiError(context: MainContext, payload: AxiosError) {
        if (payload.response!.status === 401) {
            await dispatchLogOut(context);
        }
    },
    actionRouteLoggedIn(context: MainContext, payload: { path: string | null}) {
        if (payload.path !== null) {
            router.push(payload.path);
        } else {
            if (router.currentRoute.path === '/login' || router.currentRoute.path === '/') {
                router.push('/dashboard');
                // router.push('/subscription');
            }
        }
    },
    async removeNotification(context: MainContext, payload: { notification: AppNotification, timeout: number }) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                commitRemoveNotification(context, payload.notification);
                resolve(true);
            }, payload.timeout);
        });
    },
    async actionCheckMenuAccess(context: MainContext, payload: {url: string}) {
        try {
            const response = await api.checkMenuAccess(context.state.token, payload);
            return response.data;
        } catch (error) {
            return {exists: false, allow: false}
        }
    },
    async actionGetMainMenu(context: MainContext) {
        try {
            const response = await api.getMainMenu(context.state.token);
            if (response.data) {
                commitSetMainMenu(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetDropdownMenu(context: MainContext) {
        try {
            const response = await api.getDropdownMenu(context.state.token);
            if (response.data) {
                commitSetDropdownMenu(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetAdminRegisterLink(context: MainContext) {
        try {
            const response = await api.getAdminRegisterLink();
            return response.data;
        } catch (error) {
            return '/register';
        }
    },
    async actionSetUserLanguage(context: MainContext, payload: { language: string }) {
        try {
            const response = await api.setUserLanguage(context.state.token, payload);
            return response.status === 200;
        } catch (error) {
            return false;
        }
    },
    async submitPasswordRecovery(context: MainContext, payload: { email: string }) {
        try {
            const response = await api.passwordRecovery(payload.email);
            return true;
        } catch (error) {
            return false;
        }
    },
    async submitResetPassword(context: MainContext, payload: { password: string, token: string }) {
        try {
            const response = await api.resetPassword(payload.password, payload.token);
            return true;
        } catch (error) {
            return false;
        }
    },
    async actionGetMyStructureUsers(
        context: MainContext,
        payload: { rowQuery: string, data: { search?: string, view_user_id?: any, group?: any, sort_by?: string }},
    ) {
        try {
            const response = await api.getMyStructureUsers(context.state.token, payload.data, payload.rowQuery);
            if (response.data) {
                commitSetStructureResponse(context, response.data);
            }
            return true;
        } catch (error) {
            await dispatchCheckApiError(context, error);
            dispatchCheckFormError(context, error);
            commitSetStructureResponse(context, {
                count: 0, next: 0, previous: 0, current: 0,
                results: [], columns: [], tags_groups_columns: [],
                total_count: 0, total_donations_current: 0, total_donations_prev: 0,
            });
            return false;
        }
    },
    async actionGetGetContactDonations(
        context: MainContext, payload: { data: {user_id: number}, rowQuery: string, },
    ) {
        try {
            const response = await api.getGetContactDonations(context.state.token, payload.data, payload.rowQuery);
            return response.data;
        } catch (error) {
            await dispatchCheckApiError(context, error);
            return [];
        }
    },
    async actionGetGetContactTasks(
        context: MainContext, payload: { user_id: number },
    ) {
        try {
            const response = await api.getGetContactTasks(context.state.token, payload);
            return response.data;
        } catch (error) {
            await dispatchCheckApiError(context, error);
            return [];
        }
    },
    async actionGetWaitingUsers(
        context: MainContext,
        payload: { rowQuery: string, data: { search?: string }},
    ) {
        try {
            const response = await api.getWaitingUsers(context.state.token, payload.data, payload.rowQuery);
            if (response.data) {
                commitSetWaitingUsersResponse(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetStructureUserDetails(context: MainContext, payload: { user_id: number }) {
        try {
            const response = await api.getUserDetails(context.state.token, payload);
            if (response.data) {
                commitSetStructureUserDetails(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetUserActionLogs(context: MainContext, payload: { rowQuery: string, data: { user_id: number }}) {
        try {
            const response = await api.getUserActionLogs(context.state.token, payload.data, payload.rowQuery);
            if (response.data) {
                commitSetActionLogsResponse(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetAllConfigs(context: MainContext) {
        try {
            const response = await api.getAllConfigs(context.state.token);
            commitSetAppConfigs(context, response.data);
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetAvailablePaymentMethods(context: MainContext, payload: {category?: number, country?: string}) {
        try {
            const response = await api.getAvailablePaymentMethods(context.rootState.main.token, payload);
            return response.data;
        } catch (err) {
            await dispatchCheckApiError(context, err);
            return [];
        }
    },
    async actionGetTagsGroupsFields(context: MainContext) {
        try {
            const response = await api.getTagsGroupsFields(context.rootState.main.token);
            return response.data;
        } catch (err) {
            await dispatchCheckApiError(context, err);
            return [];
        }
    },
    async actionGetPartnerDetail(context: MainContext, payload: {id: number}) {
        try {
            const response = await api.getPartnerDetail(context.rootState.main.token, payload);
            return response.data;
        } catch (err) {
            await dispatchCheckApiError(context, err);
            return null;
        }
    },
    async actionCheckFormError(context: MainContext, payload: AxiosError | any) {
        if (payload.response!.status === 400) {
            if (payload.response && payload.response.data) {
                let errorMsg = '';
                // @ts-ignore
                for (const [key, value] of Object.entries(payload.response.data)) {
                    // @ts-ignore
                    errorMsg += `${key} - ${value[0]}\n`;
                }
                // @ts-ignore
                this._vm.$toast.error(errorMsg);
            } else {
                // @ts-ignore
                this._vm.$toast.error('Error');
            }
        } else if (payload.response!.status === 500) {
            // @ts-ignore
            this._vm.$toast.error('Server error');
        }
    },
};

const { dispatch } = getStoreAccessors<MainState | any, State>('');

export const dispatchCheckApiError = dispatch(actions.actionCheckApiError);
export const dispatchCheckLoggedIn = dispatch(actions.actionCheckLoggedIn);
export const dispatchRegister = dispatch(actions.actionRegister);
export const dispatchGetUserProfile = dispatch(actions.actionGetUserProfile);
export const dispatchTwoFactorLogIn = dispatch(actions.actionTwoFactorLogIn);
export const dispatchLogIn = dispatch(actions.actionLogIn);
export const dispatchLogInByToken = dispatch(actions.actionLogInByToken);
export const dispatchSubmitResetCredsByToken = dispatch(actions.actionSubmitResetCredsByToken);
export const dispatchLogOut = dispatch(actions.actionLogOut);
export const dispatchUserLogOut = dispatch(actions.actionUserLogOut);
export const dispatchRemoveLogIn = dispatch(actions.actionRemoveLogIn);
export const dispatchRouteLoggedIn = dispatch(actions.actionRouteLoggedIn);
export const dispatchRouteLogOut = dispatch(actions.actionRouteLogOut);
export const dispatchUpdateUserProfile = dispatch(actions.actionUpdateUserProfile);
export const dispatchConnectGoogleAuthenticator = dispatch(actions.actionConnectGoogleAuthenticator);
export const dispatchDisconnectGoogleAuthenticator = dispatch(actions.actionDisconnectGoogleAuthenticator);
export const dispatchRemoveNotification = dispatch(actions.removeNotification);
export const dispatchSubmitPasswordRecovery = dispatch(actions.submitPasswordRecovery);
export const dispatchSubmitResetPassword = dispatch(actions.submitResetPassword);
export const dispatchUpdateUserAvatar = dispatch(actions.updateUserAvatar);
export const dispatchCheckMenuAccess = dispatch(actions.actionCheckMenuAccess);
export const dispatchGetMainMenu = dispatch(actions.actionGetMainMenu);
export const dispatchGetDropdownMenu = dispatch(actions.actionGetDropdownMenu);
export const dispatchGetAdminRegisterLink = dispatch(actions.actionGetAdminRegisterLink);
export const dispatchSetUserLanguage = dispatch(actions.actionSetUserLanguage);
export const dispatchGetMyStructureUsers = dispatch(actions.actionGetMyStructureUsers);
export const dispatchGetWaitingUsers = dispatch(actions.actionGetWaitingUsers);
export const dispatchGetStructureUserDetails = dispatch(actions.actionGetStructureUserDetails);
export const dispatchGetUserActionLogs = dispatch(actions.actionGetUserActionLogs);
export const dispatchGetAllConfigs = dispatch(actions.actionGetAllConfigs);
export const dispatchGetAvailablePaymentMethods = dispatch(actions.actionGetAvailablePaymentMethods);
export const dispatchGetTagsGroupsFields = dispatch(actions.actionGetTagsGroupsFields);
export const dispatchGetPartnerDetail = dispatch(actions.actionGetPartnerDetail);
export const dispatchGetGetContactDonations = dispatch(actions.actionGetGetContactDonations);
export const dispatchGetGetContactTasks = dispatch(actions.actionGetGetContactTasks);
export const dispatchCheckFormError = dispatch(actions.actionCheckFormError);
